/**
 * `/metal-resources/`: the Metal Resources landing page, built for both brands. The Ryerson build
 * renders `src/components/MetalResources/MetalResourcesLandingRyerson.tsx`; any other
 * `NEXT_PUBLIC_COMPANY` renders `MetalResourcesLandingSTS.tsx`. Each landing component sets its
 * own `MetaHead`.
 *
 * `getStaticProps` fetches, at build time, the header and footer data, the Contentful locations
 * with their metal experts (for the "Your Local Metal Professionals" section) and the blog
 * categories (`src/contentful/blog/getContentfulBlogCategoryEntries.tsx`). The STS landing ignores
 * the blog categories.
 */
import React from "react";
import MetalResourcesLandingRyerson from "@components/MetalResources/MetalResourcesLandingRyerson";
import MetalResourcesLandingSTS from "@components/MetalResources/MetalResourcesLandingSTS";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import {
	allContentfulHeaderCalls,
	getLocationsWithMetalExperts,
	HeaderPageProps,
} from "@ryerson/frontend.contentful";
import { YourMetalExpertsProps } from "@components/Shared/YourMetalExperts/YourMetalExperts";
import { getContentfulBlogCategoryEntries } from "@contentful/blog/getContentfulBlogCategoryEntries";
import { TranslatedBlogCategoryEntries } from "@contentful/blog/types";

/** Build-time props for both landing variants; see the file header. */
export const getStaticProps = async () => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			locationsWithExperts: await getLocationsWithMetalExperts(),
			blogCategories: await getContentfulBlogCategoryEntries(),
		},
	};
};

/** Picks the brand's landing component inside the standard header and footer. */
const Page: React.FC<HeaderPageProps & YourMetalExpertsProps & {blogCategories: TranslatedBlogCategoryEntries}> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			{process.env.NEXT_PUBLIC_COMPANY === "ryerson" ? (
				<MetalResourcesLandingRyerson
					blogCategories={props.blogCategories}
					locationsWithExperts={props.locationsWithExperts}
				/>
			) : (
				<MetalResourcesLandingSTS locationsWithExperts={props.locationsWithExperts} />
			)}
		</HeaderLayout>
	);
};

export default Page;
